import React, { useState, useEffect } from "react";
import {
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    List,
    ListItem as ListItemBase,
    ListItemIcon,
    ListItemText,
    Paper,
    Typography
} from "@mui/material";
import { Search } from "@mui/icons-material";
import { Alert } from '@mui/material';
import EnhancedTextField from "../../../components/EnhancedTextField";
import { utilUpdateSupplierGroup } from "../../../utils/utils";
import EnhancedErrorDialog from "../../../components/EnhancedErrorDialog";

// Relax legacy `button` prop typing on ListItem to `any` (behavior unchanged).
const ListItem: any = ListItemBase;

interface SupplierGroupOption {
    id: string;
    name: string;
    partners?: { Id: number; [key: string]: any }[];
    [key: string]: any;
}

interface AddToGroupDialogProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    availableGroups: SupplierGroupOption[];
    partnerId: number | string;
    partnerName?: string;
    onSave?: () => void;
}

export const AddToGroupDialog = (props: AddToGroupDialogProps) => {
    const { open, setOpen, availableGroups, partnerId, partnerName, onSave } = props;
    const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState({ open: false, errorHeader: "", errorMessage: "" });

    useEffect(() => {
        if (open) {
            setSelectedGroups([]);
            setSearchTerm("");
        }
    }, [open]);

    const handleToggleGroup = (groupId: string) => {
        const currentIndex = selectedGroups.indexOf(groupId);
        const newSelected = [...selectedGroups];

        if (currentIndex === -1) {
            newSelected.push(groupId);
        } else {
            newSelected.splice(currentIndex, 1);
        }

        setSelectedGroups(newSelected);
    };

    const filteredGroups = availableGroups.filter(group =>
        group.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleSubmit = async () => {
        if (selectedGroups.length === 0) {
            setError({ 
                open: true, 
                errorHeader: "Validation Error", 
                errorMessage: "Please select at least one group" 
            });
            return;
        }

        setSubmitting(true);
        const errors: string[] = [];
        const successes: string[] = [];

        for (const groupId of selectedGroups) {
            try {
                const group = availableGroups.find(g => g.id === groupId);
                if (group) {
                    // Get existing partner IDs from the group
                    const existingPartnerIds = group.partners ? group.partners.map(p => p.Id) : [];
                    
                    // Add the current partner to the group
                    const updatedPartnerIds = [...existingPartnerIds, parseInt(partnerId as string)];
                    
                    // Update the group with the new partner list
                    const result = await utilUpdateSupplierGroup(group.id, null, updatedPartnerIds);
                    
                    if (result.errorMessage) {
                        errors.push(`Failed to add to ${group.name}: ${result.errorMessage}`);
                    } else {
                        successes.push(group.name);
                    }
                }
            } catch (err: any) {
                const group = availableGroups.find(g => g.id === groupId);
                errors.push(`Failed to add to ${group?.name || groupId}: ${err.message}`);
            }
        }

        setSubmitting(false);

        if (errors.length > 0) {
            setError({
                open: true,
                errorHeader: "Some operations failed",
                errorMessage: errors.join('\n')
            });
        } else {
            setOpen(false);
            if (onSave) onSave();
        }
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={() => !submitting && setOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle style={{ paddingBottom: "8px" }}>
                    Add {partnerName || "Supplier"} to Groups
                </DialogTitle>
                <DialogContent style={{ paddingTop: "12px", paddingBottom: "12px" }}>
                    <Alert severity="info" style={{ marginBottom: "12px", padding: "8px 12px" }}>
                        <Typography variant="body2">Select one or more groups to add this supplier to.</Typography>
                    </Alert>

                    <EnhancedTextField
                        placeholder="Search groups..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{ marginBottom: "8px" }}
                        size="small"
                        disabled={submitting}
                        InputProps={{
                            startAdornment: <Search style={{ marginRight: 8, color: "#999", fontSize: 16 }} />
                        }}
                    />

                    <Paper variant="outlined" style={{ padding: 0 }}>
                        <List
                            style={{
                                maxHeight: 250,
                                overflow: "auto",
                                padding: 0
                            }}
                            dense
                        >
                            {filteredGroups.length > 0 ? (
                                filteredGroups.map((group) => (
                                    <ListItem
                                        key={group.id}
                                        button
                                        onClick={() => !submitting && handleToggleGroup(group.id)}
                                        disabled={submitting}
                                        style={{
                                            paddingTop: 6,
                                            paddingBottom: 6,
                                            borderBottom: '1px solid #f0f0f0'
                                        }}
                                    >
                                        <ListItemIcon style={{ minWidth: 36 }}>
                                            <Checkbox
                                                edge="start"
                                                checked={selectedGroups.indexOf(group.id) !== -1}
                                                tabIndex={-1}
                                                disableRipple
                                                color="primary"
                                                size="small"
                                                disabled={submitting}
                                            />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={group.name}
                                            secondary={`${group.partners?.length || 0} suppliers in this group`}
                                            slotProps={{
                                                primary: { variant: 'body2' },
                                                secondary: { variant: 'caption', color: 'textSecondary' }
                                            }} />
                                    </ListItem>
                                ))
                            ) : (
                                <ListItem>
                                    <ListItemText
                                        primary={
                                            searchTerm
                                                ? "No groups found matching your search"
                                                : "No available groups"
                                        }
                                        style={{ textAlign: "center", color: "#999" }}
                                        slotProps={{
                                            primary: { variant: 'body2' }
                                        }}
                                    />
                                </ListItem>
                            )}
                        </List>
                    </Paper>

                    {selectedGroups.length > 0 && (
                        <Typography
                            variant="caption"
                            color="textSecondary"
                            style={{ marginTop: "6px", display: 'block' }}
                        >
                            {selectedGroups.length} group{selectedGroups.length !== 1 ? 's' : ''} selected
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions style={{ padding: "12px" }}>
                    <Button
                        variant="text"
                        onClick={() => setOpen(false)}
                        disabled={submitting}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="text"
                        color="primary"
                        onClick={handleSubmit}
                        disabled={submitting || selectedGroups.length === 0}
                    >
                        {submitting ? <CircularProgress size={20} /> : "Add to Groups"}
                    </Button>
                </DialogActions>
            </Dialog>
            <EnhancedErrorDialog
                open={error.open}
                errorHeader={error.errorHeader}
                errorMessage={error.errorMessage}
                onClose={() => setError({ open: false, errorHeader: "", errorMessage: "" })}
            />
        </>
    );
};